import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { submissionChannelIds } from '../../../models/content-submission/atoms';
import { NormalizedChannel } from '../../../models/channels/types';

import { Checkbox, Icon } from '@socialchorus/shared-ui-components';
import { KeyboardEventHandler } from 'react';
import { getChannelType } from './hooks';

type ChannelsChannelProps = {
  idPrefix: string;
  channel: NormalizedChannel;
  disabled?: boolean;
  isTopicLimitReached?: boolean;
  onChange?: (id: string, val: boolean) => void;
  publicationTip?: boolean;
  // When provided, bypasses Recoil and uses controlled selection instead
  isSelected?: boolean;
  onToggle?: () => void;
};

const ChannelsChannel = ({
  idPrefix,
  channel,
  disabled,
  isTopicLimitReached,
  onChange,
  publicationTip,
  isSelected: isSelectedProp,
  onToggle,
}: ChannelsChannelProps) => {
  const { t } = useTranslation();
  const [selectedChannelIds, setSelectedChannelIds] =
    useRecoilState<number[]>(submissionChannelIds);

  const isControlled = isSelectedProp !== undefined && onToggle !== undefined;
  const isSelected = isControlled
    ? isSelectedProp
    : selectedChannelIds.includes(channel.id);

  const elementId = `${idPrefix}--channel-${channel.id}`;
  const isOptionDisabled = !!disabled || (isTopicLimitReached && !isSelected);

  const handleSelect = () => {
    if (isTopicLimitReached && !isSelected) {
      return;
    }

    if (isControlled) {
      onToggle();
    } else {
      const channelIds = !isSelected
        ? [...selectedChannelIds, channel.id]
        : selectedChannelIds.filter((cid) => cid !== channel.id);
      setSelectedChannelIds(channelIds);
    }
    onChange?.(elementId, !isSelected);
  };

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'ArrowUp') {
      document.activeElement?.previousElementSibling instanceof HTMLElement &&
        document.activeElement.previousElementSibling.focus();
    }

    if (e.key === 'ArrowDown') {
      document.activeElement?.nextElementSibling instanceof HTMLElement &&
        document.activeElement.nextElementSibling.focus();
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      document.activeElement instanceof HTMLElement &&
        !disabled &&
        document.activeElement.click();
    }
  };

  const followerCountLabel = `${channel.follower_count} ${
    channel.follower_count > 1
      ? t('screens.explore.followers.other')
      : t('screens.explore.followers.one')
  }`;

  return (
    <div
      id={elementId}
      className={cx('content-submission-channel', {
        disabled: isOptionDisabled,
      })}
      role="option"
      tabIndex={-1} // Listbox options are navigated using arrow key events: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      aria-selected={isSelected}
      aria-disabled={isOptionDisabled}
      aria-labelledby={`${elementId}--title`}
      aria-describedby={[
        `${elementId}--description`,
        `${elementId}--followers`,
      ].join(' ')}
    >
      <div
        className="content-submission-channel__image"
        style={{
          backgroundImage: channel.background_image_url
            ? `url(${channel.background_image_url})`
            : undefined,
          backgroundColor: !channel.background_image_url
            ? channel.background_color
            : undefined,
        }}
      >
        <img
          src={channel.background_image_url ?? undefined}
          alt={channel.name}
          className="image"
        />
      </div>

      <div className="content-submission-channel__body">
        <div
          id={`${elementId}--title`}
          className="content-submission-channel__title"
        >
          {channel.name}
        </div>

        <div
          id={`${elementId}--description`}
          className="content-submission-channel__description"
        >
          {channel.description}
        </div>

        <div
          id={`${elementId}--followers`}
          aria-label={followerCountLabel}
          className="content-submission-channel__followers"
        >
          <Icon>people</Icon>
          {channel.follower_count}
          {publicationTip && (
            <div className="content-submission-channel__publication_tip">
              {t(
                `content_submission.channel_tooltip.${getChannelType(channel)}`
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className="content-submission-channel__selector"
        aria-hidden="true"
        // Radix's Checkbox bubbles a native click on every `checked` change (form
        // integration); stop it here so it can't re-enter handleSelect's onClick.
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          disabled={isOptionDisabled}
          tabIndex={-1}
        />
      </div>
    </div>
  );
};

export default ChannelsChannel;
